<template>
  <component
    :is="asButton ? 'button' : 'span'"
    :type="asButton ? 'button' : undefined"
    :class="wrapperClass"
    :disabled="disabled"
    :aria-label="ariaLabel"
    v-bind="passthroughAttrs"
    @click="handleClick"
  >
    <SvgIcon
      :icon="icon"
      :stroke-width="strokeWidth"
      :cursor-pointer="asButton || cursorPointer"
      :class="iconClass"
    />
  </component>
</template>

<script setup>
import { computed, useAttrs } from 'vue';
import SvgIcon from '@/assets/SvgIcon.vue';

defineOptions({ inheritAttrs: false });

const props = defineProps({
  /** Asset basename under `@/assets/svg/` (no `.svg`). */
  icon: { type: String, required: true },
  size: {
    type: String,
    default: 'md',
    validator: v => ['xs', 'sm', 'md', 'lg'].includes(v),
  },
  strokeWidth: { type: [String, Number], default: 2 },
  cursorPointer: { type: Boolean, default: true },
  /** Render as a clickable button with hover/focus states. */
  asButton: { type: Boolean, default: false },
  /** Button disabled state (only when asButton=true). */
  disabled: { type: Boolean, default: false },
  /** Accessible label for button mode. */
  ariaLabel: { type: String, default: undefined },
  /** Color variant for button mode. */
  variant: {
    type: String,
    default: 'default',
    validator: v => ['default', 'primary', 'danger'].includes(v),
  },
});

const emit = defineEmits(['click']);

const attrs = useAttrs();

const SIZE_CLASS = {
  xs: 'h-3 w-3',
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
};

const BUTTON_SIZE_CLASS = {
  xs: 'h-6 w-6',
  sm: 'h-8 w-8',
  md: 'h-9 w-9',
  lg: 'h-10 w-10',
};

const VARIANT_CLASS = {
  default: 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
  primary: 'text-primary hover:bg-primary/10 hover:text-primary-hover',
  danger: 'text-status-red hover:bg-status-red/10 hover:text-status-red-hover',
};

const iconClass = computed(() => [SIZE_CLASS[props.size]]);

const wrapperClass = computed(() => {
  if (!props.asButton) {
    return [SIZE_CLASS[props.size], attrs.class];
  }
  return [
    'inline-flex items-center justify-center rounded-full transition-colors',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
    BUTTON_SIZE_CLASS[props.size],
    props.disabled
      ? 'cursor-not-allowed opacity-50'
      : ['cursor-pointer', VARIANT_CLASS[props.variant]],
    attrs.class,
  ];
});

const passthroughAttrs = computed(() => {
  const out = { ...attrs };
  delete out.class;
  return out;
});

function handleClick(e) {
  if (props.asButton && !props.disabled) {
    emit('click', e);
  }
}
</script>
